import DNAPricingTable from "@/components/DNAPricingTable";
import GenderRevealPricingTable from "@/components/GenderRevealPricingTable";
import FlippingGallery from "@/components/FlippingGallery";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { CheckCircle, Clock, Shield, Award } from "lucide-react";
import SEO from "@/components/SEO";
const DNAPricing = () => {
  const features = [{
    icon: CheckCircle,
    title: "AABB Accredited",
    description: "All tests processed by AABB-accredited laboratories ensuring the highest standards"
  }, {
    icon: Clock,
    title: "Fast Results",
    description: "Most test results available within 1-3 business days"
  }, {
    icon: Shield,
    title: "Chain of Custody",
    description: "Legal tests include proper chain of custody documentation for court use"
  }, {
    icon: Award,
    title: "99.9% Accuracy",
    description: "Industry-leading accuracy rates with comprehensive quality controls"
  }];
  return <>
    <SEO 
      title="DNA Testing Pricing | NearLab Services"
      description="Transparent pricing for DNA paternity testing, grandparentage, sibling tests, and more. AABB-accredited lab results with mobile collection available."
      keywords="DNA testing prices, paternity test cost, DNA test pricing, mobile DNA testing, AABB accredited, Texas DNA testing"
      url="https://nearlabservices.com/dna-pricing"
    />
    <div className="min-h-screen bg-nearlab-gray">
      {/* Hero Section */}
      <div className="bg-nearlab-teal text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <Badge variant="outline" className="mb-6 text-white border-white/30 bg-white/10">
            Professional DNA Testing Services
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">DNA Testing You Can Trust</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">Mobile and in office lab testing for families and individuals in all 50 states. All tests are processed by AABB accredited laboratories for reliable, accurate results.</p>
          <Link 
            to="/booking"
            className="inline-block bg-nearlab-accent hover:bg-nearlab-accent-light text-nearlab-teal-dark px-6 py-2 rounded-lg font-semibold transition-colors"
          >
            Book Appointment
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {features.map(feature => <Card key={feature.title} className="text-center bg-white border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="mx-auto w-12 h-12 bg-nearlab-teal-light rounded-full flex items-center justify-center mb-3">
                  <feature.icon className="w-6 h-6 text-nearlab-teal" />
                </div>
                <CardTitle className="text-lg text-nearlab-teal-dark">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </CardContent>
            </Card>)}
        </div>

      {/* Gallery */}
      <FlippingGallery />

      {/* DNA Pricing Table */}
      <DNAPricingTable />

      {/* Gender Reveal Pricing Table */}
      <div className="mt-8">
        <GenderRevealPricingTable />
      </div>


        {/* Additional Information */}
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <Card className="bg-white border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-nearlab-teal-dark">What's Included</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-nearlab-teal mt-0.5 flex-shrink-0" />
                  <span>Mobile sample collection at your location</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-nearlab-teal mt-0.5 flex-shrink-0" />
                  <span>AABB-accredited laboratory processing</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-nearlab-teal mt-0.5 flex-shrink-0" />
                  <span>Secure online results portal</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-nearlab-teal mt-0.5 flex-shrink-0" />
                  <span>Expert support throughout the process</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-nearlab-teal mt-0.5 flex-shrink-0" />
                  <span>Court-admissible documentation (legal tests)</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-nearlab-teal-dark">Why Choose NearLab?</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <Award className="w-5 h-5 text-nearlab-accent mt-0.5 flex-shrink-0" />
                  <span>AABB accredited laboratories ensuring quality</span>
                </li>
                <li className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-nearlab-accent mt-0.5 flex-shrink-0" />
                  <span>Strict chain of custody procedures</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-5 h-5 text-nearlab-accent mt-0.5 flex-shrink-0" />
                  <span>Fast turnaround times</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-nearlab-accent mt-0.5 flex-shrink-0" />
                  <span>Confidential and secure process</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Contact Section */}
        <div className="mt-12 text-center bg-white rounded-lg p-8 shadow-md">
          <h2 className="text-2xl font-bold text-nearlab-teal-dark mb-4">
            Have Questions About DNA Testing?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Our DNA testing experts are here to help you choose the right test for your needs. 
            Contact us for personalized guidance and to schedule your appointment.
          </p>
          <div className="flex flex-col sm:flex-row gap-8 justify-center">
            <div className="text-center">
              <p className="font-semibold text-nearlab-teal">Call Us</p>
              <p className="text-gray-700 text-lg">(713) 510-4442</p>
            </div>
            <div className="text-center">
              <p className="font-semibold text-nearlab-teal">Email Us</p>
              <p className="text-gray-700 text-lg">info@nearlabservices.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>;
};
export default DNAPricing;